// Historia 120 — Bloque 16/19 (Jupiter, paquete P2). THE only call site for the batch routes this
// delivery consumes (`03-dominio.md` §8.0/§8.2, confirmed verbatim in `contrato-sun.md` §1/§2/§3).
//
// Five routes, N1-N5, all via `apiPost`/`apiPut` from `generic/Api_Functions` (which already
// prepends `/endpoints/`, attaches the JWT and redirects on 401/502/503/504 -- nothing here
// duplicates or bypasses that). Every function is a thin pass-through: no retry, no shape
// transformation, no error handling of its own -- a rejection propagates exactly as
// `Api_Functions` produced it, so the caller can present it (§7.2 #15-bis: reasons/messages are
// translated by the ONE shared renderer, never here).
//
// ⚠️ N6 (`GET mos/jobBatch/{batchId}`) and N7 (`GET mos/jobBatch/byJobReference/{jobReferenceId}`)
// exist in the frozen contract but are DELIBERATELY NOT declared here: D-I12/R-13 -- the batch
// ledger has no screen in this story, and no Jupiter scenario exercises them. Do not add a sixth
// function "for completeness" -- that would be unrequested scope (`03-dominio.md` §8.0, rule #1).
// If a future package believes it needs a route that is not one of these five, STOP and escalate:
// it does not exist yet.

import { apiPost, apiPut } from '../../../../generic/Api_Functions'

const BATCH_START_PREVIEW_PATH = 'mos/deviceFabricationOrder/batchStart/preview'
const BATCH_START_PATH = 'mos/deviceFabricationOrder/batchStart'
const BATCH_PAUSE_PATH = 'mos/deviceFabricationOrder/batchPause'
const BATCH_FINISH_PREVIEW_PATH = 'mos/deviceFabricationOrder/batchFinish/preview'
const BATCH_FINISH_PATH = 'mos/deviceFabricationOrder/batchFinish'

/**
 * N1 — `POST mos/deviceFabricationOrder/batchStart/preview`. Previews the three selection paths
 * (explicit jobs, planning-group shortcut, resuming a batch) WITHOUT writing anything (INV9).
 *
 * @param {{
 *   deviceId: number,
 *   workerId: number,
 *   selectionType: 'JOBS'|'PLANNING_GROUP'|'BATCH',
 *   jobs?: Array<{fabricationOrderId: number, jobReferenceId: number}> | null,
 *   planningGroup?: string | null,
 *   originJob?: {fabricationOrderId: number, jobReferenceId: number} | null,
 *   batchId?: string | null
 * }} request `jobs` only with `selectionType === 'JOBS'`; `planningGroup` + `originJob` only with
 *   `'PLANNING_GROUP'`; `batchId` only with `'BATCH'`. ⚠️ NO mode field of any kind (D-SP15) -- N1
 *   evaluates with each job's own DEFAULT mode (`managePreparation`), never a mode the client sends.
 * @returns {Promise<{
 *   jobs: Array<{
 *     fabricationOrderId: number,
 *     jobReferenceId: number,
 *     fabricationOrderCode: string | null,
 *     partReferenceCode: string | null,
 *     nodeName: string | null,
 *     plannedStartDate: string | null,
 *     planningGroup: string | null,
 *     outcome: 'STARTABLE'|'ALREADY_ACTIVE'|'NEEDS_DECISION'|'EXCLUDED',
 *     reasons: string[],
 *     activeDevices: Array<{id: number, name: string}>,
 *     managePreparation: boolean
 *   }>,
 *   modeChoiceRequired: boolean,
 *   startableCount: number
 * }>} `reasons` is non-empty iff `outcome === 'EXCLUDED'`; `activeDevices` is non-empty iff
 *   `outcome === 'NEEDS_DECISION'`. `managePreparation` is the LAST field of each row, absent ⇒
 *   `false` (RN26.1: it is never a reason for exclusion). The date field is `plannedStartDate` --
 *   there is no `deliveryDate` anywhere in this contract (G1-2).
 */
export const previewBatchStart = (request) => apiPost(BATCH_START_PREVIEW_PATH, request)

/**
 * N2 — `PUT mos/deviceFabricationOrder/batchStart`. Starts the EXPLICIT confirmed list (no
 * `selectionType`: POL4/RN18, this route never re-resolves a group or a batch). Best-effort per
 * gesture, atomic per job (RN19/RN19.1).
 *
 * @param {{
 *   deviceId: number,
 *   workerId: number,
 *   jobs: Array<{fabricationOrderId: number, jobReferenceId: number, statusId: number}>
 * }} request `statusId` travels PER JOB (§8.2-bis) -- Sun opens exactly the status it is given, it
 *   never resolves or overwrites the mode. Build this with `batchContract.buildBatchStartRequest`
 *   so the job list can never be empty, never repeat a pair, and every `statusId` is an active
 *   status resolved by code (S179).
 * @returns {Promise<{
 *   type: 'SUCCESS',
 *   batchId: string | null,
 *   started: Array<{fabricationOrderId: number, jobReferenceId: number}>,
 *   notStarted: Array<{fabricationOrderId: number, jobReferenceId: number, reasons: string[]}>,
 *   warnings: string[]
 * }>} N2 has no `BLOCKED` branch (that is N3/N5, the two all-or-nothing gestures). `batchId` is
 *   `null` when `started.length < 2` (RN54/INV3) OR when the `STARTED` fact failed to persist even
 *   though `≥ 2` jobs started (E10) -- in that second case `warnings` carries exactly one element,
 *   `BatchRecordNotPersisted` (E-B7). The gesture-wide rejections (`BatchSelectionEmpty`,
 *   `BatchDuplicateJob`, `BatchInvalidTargetStatus`, `JobNotInDeviceQueue`,
 *   `JobGovernedByAcceptance`, the external-circuit literal) travel as `400 MessageDto{message}`,
 *   NEVER inside this 200 body.
 */
export const startBatch = (request) => apiPut(BATCH_START_PATH, request)

/**
 * N3 — `PUT mos/deviceFabricationOrder/batchPause`. Pauses ALL the batch's effective members,
 * all-or-nothing (G1-4/RN31). Writes nothing towards the batch ledger itself (POL6).
 *
 * @param {{
 *   deviceId: number,
 *   workerId: number,
 *   batchId: string,
 *   jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>
 * }} request `jobs` is used only for the contract checks (E-B1/E-B5), never to decide who gets
 *   paused (always every effective member, derived server-side from `batchId`).
 * @returns {Promise<
 *   {type: 'SUCCESS', paused: Array<{fabricationOrderId: number, jobReferenceId: number}>} |
 *   {type: 'BLOCKED', jobs: Array<{fabricationOrderId: number, jobReferenceId: number, reasons: string[]}>}
 * >} Exactly one of `paused`/`jobs` is present; the other is ABSENT from the JSON
 *   (`@JsonInclude(NON_NULL)`), never `null`/`[]`. `type: 'BLOCKED'` implies ZERO writes and ZERO
 *   state changes (D-SP14) -- render it with the SAME component used for N5's `BLOCKED` (S178): the
 *   two shapes are identical on purpose. The gesture-wide rejection (`BatchNoLongerActive`,
 *   `BatchSelectionEmpty`, `BatchDuplicateJob`) travels as `400 MessageDto{message}`, never here.
 */
export const pauseBatch = (request) => apiPut(BATCH_PAUSE_PATH, request)

/**
 * N4 — `POST mos/deviceFabricationOrder/batchFinish/preview`. Previews the close of every
 * effective member of the batch, WITHOUT writing anything (INV9).
 *
 * @param {{deviceId: number, workerId: number, batchId: string}} request No job list -- the
 *   effective members are derived server-side from `batchId`, same as N3.
 * @returns {Promise<{
 *   jobs: Array<{
 *     fabricationOrderId: number,
 *     jobReferenceId: number,
 *     fabricationOrderCode: string | null,
 *     partReferenceCode: string | null,
 *     nodeName: string | null,
 *     reportedQuantity: number,
 *     pendingQuantity: number,
 *     readiness: 'READY'|'NOT_READY',
 *     reasons: string[],
 *     partsToReportNok: number
 *   }>,
 *   allReady: boolean,
 *   totalPartsToReportNok: number
 * }>} `reasons` is non-empty iff `readiness === 'NOT_READY'`. `allReady` is all-or-nothing (RN43):
 *   one `NOT_READY` row sets it to `false`, with no nuance. `totalPartsToReportNok` is the sum
 *   across all rows, so the NOK confirmation (N5) is asked ONCE per batch.
 */
export const previewBatchFinish = (request) => apiPost(BATCH_FINISH_PREVIEW_PATH, request)

/**
 * N5 — `PUT mos/deviceFabricationOrder/batchFinish`. Finishes ALL the batch's effective members,
 * all-or-nothing (RN43/RN43.1), plus the `FINISHED` fact if `finished.length >= 2` (RN54/INV3).
 *
 * @param {{
 *   deviceId: number,
 *   workerId: number,
 *   batchId: string,
 *   jobs: Array<{fabricationOrderId: number, jobReferenceId: number}>,
 *   confirmReportIncompleteAsNok: boolean
 * }} request `confirmReportIncompleteAsNok` is a primitive boolean, absent ⇒ `false` -- same as
 *   today's individual finish. `jobs` is used only for the contract checks (E-B1/E-B5), never to
 *   decide who gets finished (always every effective member).
 * @returns {Promise<
 *   {type: 'SUCCESS', finished: Array<{fabricationOrderId: number, jobReferenceId: number}>, warnings: string[]} |
 *   {type: 'CONFIRMATION_REQUIRED', incompletePartsToReportNok: number, jobs: Array<{fabricationOrderId: number, jobReferenceId: number, incompletePartsToReportNok: number}>} |
 *   {type: 'BLOCKED', jobs: Array<{fabricationOrderId: number, jobReferenceId: number, reasons: string[]}>}
 * >} Exactly one of the three groups is present; the other two are ABSENT from the JSON
 *   (`@JsonInclude(NON_NULL)`). `CONFIRMATION_REQUIRED` writes nothing (ask once, D-I9/RN48).
 *   `type: 'BLOCKED'` implies ZERO writes and ZERO state changes (D-SP14) -- render it with the
 *   SAME component used for N3's `BLOCKED` (S178): the two shapes are identical on purpose. The
 *   gesture-wide rejection (`BatchNoLongerActive`, `BatchSelectionEmpty`, `BatchDuplicateJob`)
 *   travels as `400 MessageDto{message}`, never inside this 200 body.
 */
export const finishBatch = (request) => apiPut(BATCH_FINISH_PATH, request)
